import { useState } from 'react';
import { motion } from 'framer-motion';
import { Code, Search, Shield, Zap, AlertTriangle, CheckCircle, Loader } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CodeReviewPage() {
  const { getAuthHeaders } = useAuth();
  const [codeInput, setCodeInput] = useState('');
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleReview = async () => {
    if (!codeInput.trim()) {
      toast.error('Please enter code to review');
      return;
    }

    setLoading(true);
    setReview(null);

    try {
      const response = await axios.post(`${API_URL}/ai/review`, {
        diff: codeInput,
        project_context: {
          language: 'JavaScript/Python',
          framework: 'React/FastAPI'
        }
      }, {
        headers: getAuthHeaders()
      });

      setReview(response.data);
    } catch (error) {
      console.error('Code review failed:', error);
      toast.error('Failed to review code');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-accent bg-accent/20 border-accent/30';
      case 'approved-with-warnings': return 'text-yellow-500 bg-yellow-500/20 border-yellow-500/30';
      case 'rejected': return 'text-destructive bg-destructive/20 border-destructive/30';
      default: return 'text-primary bg-primary/20 border-primary/30';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3" data-testid="code-review-title">
            <Code className="w-8 h-8 text-secondary" />
            AI Code Review
          </h1>
          <p className="text-foreground/60 mt-1">
            Production-focused code analysis for security, performance, and readiness
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Card className="bg-card/50 border-border h-full">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Code className="w-5 h-5 text-primary" />
                  Code / Diff Input
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  placeholder="Paste your code diff or changes here...

Example:
+ async function processPayment(userId, amount) {
+   const user = await db.users.findOne({ id: userId });
+   const result = await stripe.charges.create({
+     amount: amount * 100,
+     currency: 'usd',
+     customer: user.stripeId
+   });
+   return result;
+ }"
                  className="min-h-[300px] bg-[#0A0E27] border-border font-code text-sm resize-none"
                  data-testid="code-input"
                />
                <Button
                  onClick={handleReview}
                  disabled={loading || !codeInput.trim()}
                  className="w-full bg-secondary text-white font-bold hover:bg-secondary/90"
                  data-testid="review-code-btn"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      Reviewing...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      Review Code
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Review Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Card className="bg-card/50 border-border h-full">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-accent" />
                  Review Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!review && !loading && (
                  <div className="text-center py-16">
                    <Code className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">
                      Paste your code and click review for AI-powered analysis
                    </p>
                  </div>
                )}

                {loading && (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Analyzing code for production readiness...</p>
                  </div>
                )}

                {review && (
                  <div className="space-y-6">
                    {/* Overall Score */}
                    <div className="p-4 bg-[#0A0E27] rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-heading font-semibold text-white">Safety Score</h4>
                        <span className="text-2xl font-bold text-primary">
                          {review.overallSafetyScore || 85}/100
                        </span>
                      </div>
                      <Progress 
                        value={review.overallSafetyScore || 85} 
                        className="h-2"
                      />
                    </div>

                    {/* Approval Status */}
                    {review.approvalStatus && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">Status:</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border capitalize ${getStatusColor(review.approvalStatus)}`}>
                          {review.approvalStatus.replace(/-/g, ' ')}
                        </span>
                      </div>
                    )}

                    {/* Security Analysis */}
                    {review.securityAnalysis && (
                      <div>
                        <h4 className="font-heading font-semibold text-white mb-3 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-primary" />
                          Security Analysis
                          <span className="text-xs text-muted-foreground ml-2">
                            {review.securityAnalysis.score || 90}/100
                          </span>
                        </h4>
                        {review.securityAnalysis.issues && review.securityAnalysis.issues.length > 0 ? (
                          <div className="space-y-2">
                            {review.securityAnalysis.issues.map((issue, i) => (
                              <div key={i} className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
                                <div className="flex items-start justify-between">
                                  <p className="text-sm text-foreground/80">{issue.issue || issue}</p>
                                  {issue.severity && (
                                    <span className="text-xs px-2 py-0.5 bg-destructive/20 text-destructive rounded">
                                      {issue.severity}
                                    </span>
                                  )}
                                </div>
                                {issue.location && (
                                  <p className="text-xs text-muted-foreground mt-1 font-code">
                                    {issue.location}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-3 bg-accent/5 border border-accent/20 rounded-lg flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-accent" />
                            <span className="text-sm text-accent">No security issues found</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Performance Analysis */}
                    {review.performanceAnalysis && (
                      <div>
                        <h4 className="font-heading font-semibold text-white mb-3 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-secondary" />
                          Performance
                          <span className="text-xs text-muted-foreground ml-2">
                            {review.performanceAnalysis.score || 85}/100
                          </span>
                        </h4>
                        {review.performanceAnalysis.optimizations && review.performanceAnalysis.optimizations.length > 0 && (
                          <ul className="space-y-2">
                            {review.performanceAnalysis.optimizations.map((opt, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-foreground/70">
                                <span className="text-secondary mt-1">•</span>
                                <span>{opt.type || opt}: {opt.improvement || ''}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    {/* Required Fixes */}
                    {review.requiredFixes && review.requiredFixes.length > 0 && (
                      <div>
                        <h4 className="font-heading font-semibold text-white mb-3 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-yellow-500" />
                          Required Before Deploy
                        </h4>
                        <ul className="space-y-2">
                          {review.requiredFixes.map((fix, i) => (
                            <li key={i} className="p-2 bg-yellow-500/5 border border-yellow-500/20 rounded text-sm text-foreground/80">
                              {fix}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Deployment Recommendation */}
                    {review.deploymentRecommendation && (
                      <div className={`p-4 rounded-lg border ${
                        review.deploymentRecommendation === 'deploy-now' 
                          ? 'bg-accent/10 border-accent/30' 
                          : review.deploymentRecommendation === 'do-not-deploy'
                          ? 'bg-destructive/10 border-destructive/30'
                          : 'bg-yellow-500/10 border-yellow-500/30'
                      }`}>
                        <div className="flex items-center gap-2">
                          {review.deploymentRecommendation === 'deploy-now' ? (
                            <CheckCircle className="w-5 h-5 text-accent" />
                          ) : review.deploymentRecommendation === 'do-not-deploy' ? (
                            <AlertTriangle className="w-5 h-5 text-destructive" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-yellow-500" />
                          )}
                          <span className="font-medium text-white capitalize">
                            {review.deploymentRecommendation.replace(/-/g, ' ')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
}
